import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core'
import { formatInrRupeeCompact } from '@core/helpers/format.helper'
import {
    SHIKSHA_PIPELINE_RISK_RUPEE_COMPACT_FORMAT,
    SHIKSHA_SALES_FUNNEL_CONTENT,
    SHIKSHA_SALES_FUNNEL_STAGES,
    SHIKSHA_SALES_FUNNEL_TABLE_STAGE_TRANSITIONS,
    SHIKSHA_SALES_FUNNEL_UI,
} from '../constants/shiksha-dashboard.constants'
import type {
    IShikshaSalesFunnelStage,
    IShikshaSalesFunnelViewModel,
} from '@features/home/models/shiksha-dashboard.model'
import { ShikshaDashboardSubtitleHeaderComponent } from '../shiksha-dashboard-subtitle-header/shiksha-dashboard-subtitle-header.component'
import { SvgIconComponent } from '@app/shared/components/svg-icon/svg-icon.component'

@Component({
    selector: 'app-shiksha-sales-funnel',
    standalone: true,
    templateUrl: './shiksha-sales-funnel.component.html',
    styleUrl: './shiksha-sales-funnel.component.scss',
    imports: [ShikshaDashboardSubtitleHeaderComponent, SvgIconComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShikshaSalesFunnelComponent {
    readonly content = SHIKSHA_SALES_FUNNEL_CONTENT
    readonly uiConfig = SHIKSHA_SALES_FUNNEL_UI
    readonly stages = input<readonly IShikshaSalesFunnelStage[]>(SHIKSHA_SALES_FUNNEL_STAGES)
    readonly viewMode = signal<'funnel' | 'table'>('funnel')

    readonly viewStages = computed<readonly IShikshaSalesFunnelViewModel[]>(() => {
        const stages = this.stages()

        const resolvedStages = stages.map((stage) => {
            const normalizedCount = Math.max(stage.count, 0)
            const normalizedLost = Math.max(stage.lostToNextCount, 0)
            const inProgressFromApi = Math.max(stage.inProgressCount ?? 0, 0)
            const movedToNextFromApi = Math.max(stage.movedToNextCount ?? 0, 0)
            const totalEnteredCount = Math.max(
                normalizedCount,
                inProgressFromApi + normalizedLost + movedToNextFromApi,
            )
            const inProgressCountResolved = Math.min(inProgressFromApi, totalEnteredCount)
            const dealsLost = Math.min(
                normalizedLost,
                Math.max(totalEnteredCount - inProgressCountResolved, 0),
            )
            const movedToNextCountResolved = Math.max(
                Math.min(
                    movedToNextFromApi,
                    Math.max(totalEnteredCount - inProgressCountResolved - dealsLost, 0),
                ),
                0,
            )
            const wonCountResolved =
                stage.stageCode === 'S5'
                    ? Math.max(stage.wonCount ?? totalEnteredCount, totalEnteredCount)
                    : movedToNextCountResolved
            const showFunnelValue =
                (stage.stageCode === 'S4' || stage.stageCode === 'S5') &&
                Number.isFinite(stage.dealWonValue ?? NaN)
            const dealWonValueDisplay = showFunnelValue
                ? this.formatInrCompact(stage.dealWonValue as number)
                : null

            return {
                ...stage,
                conversion: '0%',
                totalEnteredCount,
                dealWonValueDisplay,
                dealsLost,
                wonCountResolved,
                inProgressCountResolved,
                movedToNextCountResolved,
                progressPercent: 0,
                activeCount: stage.stageCode === 'S0' ? null : inProgressCountResolved,
            }
        })

        return resolvedStages.map((stage, index) => {
            const nextStage = resolvedStages[index + 1]
            const conversionPercent = this.calculateStageToNextConversionPercent(
                Math.max(stage.count, 0),
                Math.max(nextStage?.count ?? 0, 0),
            )
            const progressPercent = stage.stageCode === 'S0' ? 100 : conversionPercent
            return {
                ...stage,
                conversion: this.formatConversionPercentLabel(conversionPercent),
                progressPercent: Math.max(Math.min(progressPercent, 100), 0),
            }
        })
    })

    /** Show a real percentage (including 0%) so table/funnel never use "-" for conversion / success rate. */
    private formatConversionPercentLabel(value: number): string {
        if (Number.isFinite(value) && value > 0) {
            return `${value.toFixed(1)}%`
        }
        return '0%'
    }

    /**
     * SPEC formula: Conversion (Sn -> Sn+1) = (Count of Sn+1 / Count of Sn) * 100
     */
    private calculateStageToNextConversionPercent(currentCount: number, nextCount: number): number {
        if (!Number.isFinite(currentCount) || currentCount <= 0) {
            return 0
        }
        if (!Number.isFinite(nextCount) || nextCount <= 0) {
            return 0
        }
        return Math.max(Math.min((nextCount / currentCount) * 100, 100), 0)
    }

    getConversionInfoLabel(stageCode: string): string {
        const normalizedStageCode = (stageCode ?? '').trim().toUpperCase()
        const stageNumber = Number.parseInt(normalizedStageCode.replace('S', ''), 10)
        if (!Number.isFinite(stageNumber) || stageNumber < 0) {
            return this.content.conversionTooltipFallback
        }
        return this.content.conversionTooltipTemplate
            .replace('{current}', `S${stageNumber}`)
            .replace('{next}', `S${stageNumber + 1}`)
    }

    private formatInrCompact(value: number): string {
        return formatInrRupeeCompact(value, SHIKSHA_PIPELINE_RISK_RUPEE_COMPACT_FORMAT)
    }

    readonly tableTransitions = computed(() => {
        const stages = this.viewStages()
        if (stages.length < 2) {
            return []
        }

        const byCode = new Map(stages.map((s) => [s.stageCode, s]))

        return SHIKSHA_SALES_FUNNEL_TABLE_STAGE_TRANSITIONS.flatMap(({ fromCode, toCode }) => {
            const fromStage = byCode.get(fromCode)
            const toStage = byCode.get(toCode)
            if (!fromStage || !toStage) {
                return []
            }

            const conversionPercent = this.calculateStageToNextConversionPercent(
                Math.max(fromStage.count, 0),
                Math.max(toStage.count, 0),
            )

            return [
                {
                    id: `${fromCode}-${toCode}`,
                    fromCode,
                    toCode,
                    conversionLabel: this.formatConversionPercentLabel(conversionPercent),
                    conversionPercent,
                    dealsLabel: `${toStage.totalEnteredCount}`,
                },
            ]
        })
    })
}
